import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async"; 
import Slider from "react-slick";
import { db } from "../firebase/firebaseConfig";
import {
  collection, addDoc, serverTimestamp,
  query, orderBy, getDocs, limit,
} from "firebase/firestore";
import { fetchProducts } from "../redux/productAction";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./HomePage.css";

/* ─── Data ────────────────────────────────────────────────── */
const heroItems = [
  {
    image: "images/8.jpg",
    title: "Discover\nPremium Wigs",
    subtitle: "Natural-looking, luxuriously crafted for every occasion.",
    tag: "New Collection",
  },
  {
    video: "images/video.mp4",
    title: "Watch the\nTransformation",
    subtitle: "Experience beauty in motion with our video preview.",
    tag: "Video Lookbook",
  },
  {
    image: "images/Bouncy curls.jpg",
    title: "Curls\nThat Wow",
    subtitle: "Big volume, soft texture, unbeatable confidence.",
    tag: "Bestseller",
  },
];

const features = [
  { icon: "✦", label: "100% Human Hair" },
  { icon: "◈", label: "Lace Front Finish" },
  { icon: "⬡", label: "Custom Fitting" },
  { icon: "↺", label: "30-Day Returns" },
];

/* ─── Animation variants ──────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

/* ─── Section reveal wrapper ──────────────────────────────── */
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      custom={delay}
    >
      {children}
    </motion.div>
  );
}

/* ─── Star rating ─────────────────────────────────────────── */
function StarRating({ value, onChange, readOnly = false }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="stars" aria-label={`Rating: ${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`star ${(hover || value) >= n ? "filled" : ""}`}
          onClick={() => !readOnly && onChange(n)}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          disabled={readOnly}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

/* ─── Product Card ────────────────────────────────────────── */
function ProductCard({ product, priority = false }) { // 👈 Added priority prop
  return (
    <Link to="/shop" className="hp-product-card">
      <div className="hp-product-img-wrap">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="hp-product-img"
            loading={priority ? "eager" : "lazy"} // 👈 First card loads eagerly
            fetchpriority={priority ? "high" : "auto"} // 👈 LCP hint
          />
        ) : (
          <div className="hp-product-img-placeholder">🖼</div>
        )}
        {product.soldOut && (
          <span className="hp-product-badge hp-product-badge-sold">Sold Out</span>
        )}
        {!product.soldOut && (
          <span className="hp-product-badge hp-product-badge-stock">In Stock</span>
        )}
        <div className="hp-product-overlay">
          <span className="hp-product-cta">View Product →</span>
        </div>
      </div>
      <div className="hp-product-info">
        <p className="hp-product-name" title={product.name}>{product.name}</p>
        <div className="hp-product-footer">
          <p className="hp-product-price">GH₵ {Number(product.price).toFixed(2)}</p>
          {product.quantity > 0 && !product.soldOut && (
            <p className="hp-product-qty">{product.quantity} left</p>
          )}
        </div>
      </div>
    </Link>
  );
}

/* ─── Main component ──────────────────────────────────────── */
const HomePage = () => {
  const dispatch = useDispatch();
  const { products, loading: productsLoading } = useSelector((s) => s.product);

  const [name, setName]         = useState("");
  const [review, setReview]     = useState("");
  const [rating, setRating]     = useState(5);
  const [email, setEmail]       = useState("");
  const [message, setMessage]   = useState("");
  const [reviews, setReviews]   = useState([]);
  const [newsList, setNewsList] = useState([]);
  const [sent, setSent]         = useState(false);
  const [reviewSent, setReviewSent]   = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [slide, setSlide]             = useState(0);
  const [announcementIdx, setAnnouncementIdx] = useState(0);

  const fetchReviews = async () => {
    try {
      const q = query(collection(db, "reviews"), orderBy("timestamp", "desc"), limit(6));
      const snap = await getDocs(q);
      setReviews(snap.docs.map((d) => d.data()));
    } catch (e) { console.error(e); }
  };

  const fetchNews = async () => {
    try {
      const q = query(collection(db, "news"), orderBy("timestamp", "desc"));
      const snap = await getDocs(q);
      setNewsList(snap.docs.map((d) => d.data()));
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchReviews();
    fetchNews();
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    if (newsList.length < 2) return;
    const t = setInterval(() => setAnnouncementIdx((i) => (i + 1) % newsList.length), 4000);
    return () => clearInterval(t);
  }, [newsList]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setReviewError("");
    if (!name || !review) { setReviewError("Name and review are required."); return; }
    if (review.length < 10) { setReviewError("Review must be at least 10 characters."); return; }
    try {
      await addDoc(collection(db, "reviews"), { name, review, rating, timestamp: serverTimestamp() });
      setName(""); setReview(""); setRating(5);
      setReviewSent(true);
      fetchReviews();
    } catch (e) { console.error(e); }
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { alert("Enter a valid email"); return; }
    try {
      await addDoc(collection(db, "messages"), { email, message, timestamp: serverTimestamp() });
      setEmail(""); setMessage(""); setSent(true);
    } catch (e) { console.error(e); }
  };

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 800,
    autoplay: true,
    autoplaySpeed: 4500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    beforeChange: (_, next) => setSlide(next),
    fade: true,
  };

  const featured = products.slice(0, 4);

  return (
    <div className="hp">

      {/* ── SEO Helmet ────────────────────────────────────── */}
      <Helmet>
        <title>Glossy Lux Hair | Premium Wigs & Hair Extensions in Ghana</title>
        <meta name="description" content="Shop premium human hair wigs, lace fronts and extensions at Glossy Lux Hair. 100% human hair, custom fitting, 30-day returns. Based in Winneba, Ghana." />
        <link rel="canonical" href="https://glossyluxhair.com/" />
        <meta property="og:title" content="Glossy Lux Hair | Premium Wigs & Hair Extensions" />
        <meta property="og:description" content="Shop premium human hair wigs and extensions at Glossy Lux Hair. Based in Winneba, Ghana." />
        <meta property="og:url" content="https://glossyluxhair.com/" />
        <meta property="og:image" content="https://glossyluxhair.com/images/8.jpg" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Store",
            "name": "Glossy Lux Hair",
            "url": "https://glossyluxhair.com",
            "description": "Premium human hair wigs and extensions in Ghana",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "North Campus",
              "addressLocality": "Winneba",
              "addressCountry": "GH"
            },
            "telephone": "+233530557187",
            "currenciesAccepted": "GHS",
            "priceRange": "$$"
          }
        `}</script>
      </Helmet>

      {/* ── Announcement ticker ───────────────────────────── */}
      {newsList.length > 0 && (
        <div className="hp-ticker" role="marquee" aria-live="polite">
          <span className="hp-ticker-dot" />
          <AnimatePresence mode="wait">
            <motion.span
              key={announcementIdx}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              className="hp-ticker-text"
            >
              {newsList[announcementIdx]?.message}
            </motion.span>
          </AnimatePresence>
        </div>
      )}

      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="hp-hero" aria-label="Featured collection">
        <Slider {...sliderSettings}>
          {heroItems.map((item, i) => (
            <div key={i} className="hp-hero-slide">
              <div className="hp-hero-media">
                {item.video ? (
                  <video autoPlay muted loop playsInline className="hp-hero-img">
                    <source src={item.video} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={item.image}
                    alt={item.tag} // 👈 Descriptive alt instead of empty
                    className="hp-hero-img"
                    loading={i === 0 ? "eager" : "lazy"}       // 👈 First slide eager
                    fetchpriority={i === 0 ? "high" : "auto"}  // 👈 LCP boost
                  />
                )}
                <div className="hp-hero-overlay" />
              </div>

              <div className="hp-hero-content">
                <motion.span
                  className="hp-hero-tag"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                >
                  {item.tag}
                </motion.span>
                <motion.h1
                  className="hp-hero-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                >
                  {item.title.split("\n").map((line, j) => (
                    <span key={j} className="hp-hero-line">{line}</span>
                  ))}
                </motion.h1>
                <motion.p
                  className="hp-hero-sub"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  {item.subtitle}
                </motion.p>
                <motion.div
                  className="hp-hero-actions"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <Link to="/shop" className="hp-btn hp-btn-primary">
                    Shop Collection
                    <span className="hp-btn-arrow">→</span>
                  </Link>
                  <Link to="/shop" className="hp-btn hp-btn-ghost">
                    View Lookbook
                  </Link>
                </motion.div>
              </div>
            </div>
          ))}
        </Slider>

        <div className="hp-hero-dots">
          {heroItems.map((_, i) => (
            <button
              key={i}
              className={`hp-hero-dot ${slide === i ? "active" : ""}`}
              aria-label={`Go to slide ${i + 1}`} // 👈 More descriptive aria-label
            />
          ))}
        </div>

        <div className="hp-scroll-hint" aria-hidden="true"> {/* 👈 Explicit true */}
          <span className="hp-scroll-line" />
          <span className="hp-scroll-label">Scroll</span>
        </div>
      </section>

      {/* ── Feature strip ─────────────────────────────────── */}
      <section className="hp-features" aria-label="Why choose us">
        {features.map((f, i) => (
          <Reveal key={i} delay={i * 0.08} className="hp-feature">
            <span className="hp-feature-icon" aria-hidden="true">{f.icon}</span>
            <span className="hp-feature-label">{f.label}</span>
          </Reveal>
        ))}
      </section>

      {/* ── Promo banner ──────────────────────────────────── */}
      <Reveal className="hp-promo">
        <div className="hp-promo-badge">Limited Time</div>
        <h2 className="hp-promo-title">
          20% <em>OFF</em> All Premium Wigs Today
        </h2>
        <p className="hp-promo-sub">Use code GLOSS20 at checkout. Ends midnight.</p>
        <Link to="/shop" className="hp-btn hp-btn-light">
          Claim Offer <span className="hp-btn-arrow">→</span>
        </Link>
      </Reveal>

      {/* ── Featured Products ─────────────────────────────── */}
      <section className="hp-section" aria-label="Featured products">
        <Reveal>
          <div className="hp-section-header">
            <h2 className="hp-section-title">Featured Wigs</h2>
            <Link to="/shop" className="hp-link-subtle">View all →</Link>
          </div>
        </Reveal>

        {productsLoading ? (
          <div className="hp-products-loading" role="status" aria-label="Loading products">
            <div className="hp-products-spinner" />
          </div>
        ) : featured.length === 0 ? (
          <div className="hp-products-empty">
            <span>No products available yet.</span>
          </div>
        ) : (
          <div className="hp-products-grid">
            {featured.map((product, i) => (
              <Reveal key={product.id} delay={i * 0.08}>
                <ProductCard product={product} priority={i === 0} /> {/* 👈 First card prioritised */}
              </Reveal>
            ))}
          </div>
        )}

        <Reveal>
          <div className="hp-center" style={{ marginTop: "2.5rem" }}>
            <Link to="/shop" className="hp-btn hp-btn-primary">
              Browse Full Collection <span className="hp-btn-arrow">→</span>
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ── Leave a review ────────────────────────────────── */}
      <section className="hp-section hp-section-alt" aria-label="Write a review">
        <Reveal>
          <div className="hp-section-header">
            <h2 className="hp-section-title">Share Your Experience</h2>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form className="hp-form" onSubmit={handleReviewSubmit} noValidate>
            {reviewError && (
              <div className="hp-form-error" role="alert">{reviewError}</div>
            )}
            {reviewSent && (
              <div className="hp-form-success" role="status">
                Thank you! Your review has been posted.
              </div>
            )}

            <div className="hp-form-row">
              <div className="hp-field">
                <label className="hp-label" htmlFor="r-name">Your name</label>
                <input
                  id="r-name"
                  className="hp-input"
                  placeholder="Jane D."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="hp-field">
                <label className="hp-label">Rating</label>
                <StarRating value={rating} onChange={setRating} />
              </div>
            </div>

            <div className="hp-field">
              <label className="hp-label" htmlFor="r-text">Your review</label>
              <textarea
                id="r-text"
                className="hp-input hp-textarea"
                placeholder="Tell us what you loved…"
                rows={4}
                value={review}
                onChange={(e) => setReview(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="hp-btn hp-btn-primary">
              Post Review <span className="hp-btn-arrow">→</span>
            </button>
          </form>
        </Reveal>
      </section>

      {/* ── Customer reviews ──────────────────────────────── */}
      {reviews.length > 0 && (
        <section className="hp-section" aria-label="Customer reviews">
          <Reveal>
            <h2 className="hp-section-title" style={{ marginBottom: "2rem" }}>
              Customer Reviews
            </h2>
          </Reveal>

          <div className="hp-reviews-grid">
            {reviews.map((r, i) => (
              <Reveal key={i} delay={i * 0.07} className="hp-review-card">
                <div className="hp-review-header">
                  <div className="hp-avatar" aria-hidden="true">
                    {r.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="hp-review-name">{r.name}</div>
                    <StarRating value={r.rating} readOnly />
                  </div>
                </div>
                <p className="hp-review-text">"{r.review}"</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ── Contact ───────────────────────────────────────── */}
      <section className="hp-section hp-section-alt" id="contact" aria-label="Contact us">
        <Reveal>
          <div className="hp-section-header">
            <div>
              <h2 className="hp-section-title">Get in Touch</h2>
              <p className="hp-section-sub">
                Questions, wholesale enquiries, or just want to say hello?
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form className="hp-form" onSubmit={handleMessageSubmit} noValidate>
            {sent && (
              <div className="hp-form-success" role="status">
                Message sent! We'll get back to you within 24 hours.
              </div>
            )}

            <div className="hp-field">
              <label className="hp-label" htmlFor="c-email">Email address</label>
              <input
                id="c-email"
                type="email"
                className="hp-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="hp-field">
              <label className="hp-label" htmlFor="c-msg">Message</label>
              <textarea
                id="c-msg"
                className="hp-input hp-textarea"
                placeholder="How can we help?"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <button type="submit" className="hp-btn hp-btn-primary">
              Send Message <span className="hp-btn-arrow">→</span>
            </button>
          </form>
        </Reveal>
      </section>

      {/* ── Final CTA ─────────────────────────────────────── */}
      <Reveal className="hp-cta">
        <div className="hp-cta-inner">
          <span className="hp-cta-eyebrow">Ready for your transformation?</span>
          <h2 className="hp-cta-title">Find Your Perfect Wig</h2>
          <p className="hp-cta-sub">
            Over 200 styles. Free shipping on orders over GH₵100. {/* 👈 Fixed currency */}
          </p>
          <Link to="/shop" className="hp-btn hp-btn-light hp-btn-lg">
            Shop Now <span className="hp-btn-arrow">→</span>
          </Link>
        </div>
        <div className="hp-cta-orb hp-cta-orb-1" aria-hidden="true" />
        <div className="hp-cta-orb hp-cta-orb-2" aria-hidden="true" />
      </Reveal>

    </div>
  );
};

export default HomePage;